import { CartPolicy, CartPolicyResult } from './types';
import { cartWithoutPendingRemovals, pass } from './cartLineHelpers';
import { Cart, CartLineInput, CartLineUpdateInput, PolicyCartInput } from '../types';
import { getMaxConfig, getMaxVariantIds } from 'content/products/max';

/**
 * Adding a MAX plan while the cart already holds the other MAX product
 * (monthly vs yearly are separate variants) becomes a line update so the
 * cart never carries both. Attributes are left untouched so a funnel-bundled
 * line keeps its `_bundle_id`. Shopify CartLineUpdateInput accepts a new
 * `merchandiseId` + `sellingPlanId` on the existing line.
 */
export function applyMaxPlanSwap(
  input: PolicyCartInput,
  cart: Cart | null,
  maxVariantIds: ReadonlySet<string>,
): CartPolicyResult {
  const adds = input.lineItemsToAdd;
  if (!adds?.length || !cart || maxVariantIds.size === 0) return pass(input);

  const keep: CartLineInput[] = [];
  const modify: CartLineUpdateInput[] = [...(input.lineItemsToModify ?? [])];
  const reasons: string[] = [];

  for (const line of adds) {
    if (!maxVariantIds.has(line.merchandiseId)) {
      keep.push(line);
      continue;
    }
    const existing = cart.lines.edges.find(({ node }) =>
      maxVariantIds.has(node.merchandise.id),
    )?.node;
    if (!existing) {
      keep.push(line);
      continue;
    }
    const existingPlan = existing.sellingPlanAllocation?.sellingPlan.id;
    if (
      existing.merchandise.id === line.merchandiseId &&
      existingPlan === line.sellingPlanId
    ) {
      keep.push(line);
      continue;
    }
    modify.push({
      id: existing.id,
      quantity: 1,
      merchandiseId: line.merchandiseId,
      ...(line.sellingPlanId ? { sellingPlanId: line.sellingPlanId } : {}),
    });
    reasons.push(`switch MAX plan on ${existing.id}`);
  }

  if (!reasons.length) return pass(input);
  return { input: { ...input, lineItemsToAdd: keep, lineItemsToModify: modify }, reasons };
}

export const maxPlanSwapPolicy: CartPolicy = (input, cart, meta) => {
  const cfg = getMaxConfig(meta.store);
  if (!cfg) return pass(input);
  return applyMaxPlanSwap(
    input,
    cartWithoutPendingRemovals(cart, input),
    new Set(getMaxVariantIds(cfg)),
  );
};
